import { Form, Modal, Button, Rate, Checkbox } from "antd";
import TextArea from "antd/es/input/TextArea";
import { forwardRef, memo, useImperativeHandle, useState, useRef } from "react";
import toast from "react-hot-toast";
import { useI18n } from "@/contexts/I18nContext";
import { useForm } from "antd/es/form/Form";
import { getAgentFromScript } from "@/utils";
import { editAgentSatisfaction } from "@/services";
import renderExtraContentStyles from "@/components/RenderExtraContent/index.module.scss";
import styles from "./index.module.scss";

/**
 * 用户满意度调查弹窗
 * @returns
 */
const UserSatisfactionSurveyModal = forwardRef((props: any, ref) => {
  const { closeModal, cardRef, loading, satisfaction } = props;
  const [evaluationOpen, setEvaluationOpen] = useState<boolean>(false);
  const [rateValue, setRateValue] = useState<number>(0);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [form] = useForm();
  const toastIdRef = useRef<any>(null); // 保存toast ID
  const { formatMessage } = useI18n();

  // 处理标签选择变化
  const handleTagChange = (checkedValues: string[]) => {
    setSelectedTags(checkedValues);
  };

  useImperativeHandle(ref, () => ({
    setVisit: (visible: boolean) => {
      setEvaluationOpen(visible);
    },
  }));

  const customCheckboxItem = (tagOptions: any[]) => {
    return tagOptions.map((option) => (
      <Checkbox
        key={option.field as string}
        value={option.field}
        style={{
          margin: 0,
          padding: "4px 8px",
          border: "1px solid #d9d9d9",
          borderRadius: "6px",
          fontSize: "14px",
          color: selectedTags.includes(option.field as string)
            ? "#fff"
            : "#000",
          backgroundColor: selectedTags.includes(option.field as string)
            ? "#1890ff"
            : "#f5f5f5",
          borderColor: selectedTags.includes(option.field as string)
            ? "#1890ff"
            : "#d9d9d9",
        }}
      >
        {option.name}
      </Checkbox>
    ));
  };

  return (
    <Modal
      open={evaluationOpen}
      width={"80%"}
      wrapClassName={`${renderExtraContentStyles.customModalWrap} ${styles.evaluationModal}`}
      getContainer={() => cardRef?.current || document.body}
      onCancel={() => {
        // 关闭弹窗时销毁当前的toast提示
        if (toastIdRef.current) {
          toast.dismiss(toastIdRef.current);
          toastIdRef.current = null;
        }
        setEvaluationOpen(false);
        closeModal();
      }}
      footer={
        <Button
          block
          id="close_to_agent_button"
          data-track-element
          type="primary"
          loading={loading}
          onClick={async () => {
            form.submit();
          }}
        >
          {formatMessage("submitText")}
        </Button>
      }
    >
      <div style={{ fontWeight: "bold", fontSize: 18, marginTop: 12 }}>
        {satisfaction.title}
      </div>
      <Form
        form={form}
        onFinish={async (values) => {
          const currentTags =
            rateValue > 3 ? satisfaction.tag4 : satisfaction.tag1;

          // 过滤出当前field相同的项
          const filteredList = currentTags.filter((item: any) =>
            values?.tags?.some((tag: any) => tag === item.field),
          );

          const resultValue = {
            ...values,
            tags: filteredList.map((item: any) => {
              return {
                field: item.field,
                name: item.name,
              };
            }),
          };
          try {
            await editAgentSatisfaction({
              agent: getAgentFromScript(),
              ...resultValue,
            });
            // 保存toast ID，以便后续可以销毁
            toastIdRef.current = toast.success(
              formatMessage("submittedSuccessfully"),
            );
            // 等待1秒后关闭弹窗
            setTimeout(() => {
              // 关闭弹窗前销毁toast
              if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = null;
              }
              closeModal();
            }, 1000);
          } catch (error) {
            console.error("提交失败:", error);
          }
        }}
        onFinishFailed={({ errorFields }: any) => {
          const firstError = errorFields[0]?.errors[0];
          if (firstError) {
            toast.error(firstError);
          }
        }}
      >
        <Form.Item
          name="score"
          rules={[
            {
              required: true,
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject("请进行评分!");
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <Rate onChange={(value) => setRateValue(value)} value={rateValue} />
        </Form.Item>
        {rateValue > 0 && (
          <Form.Item name="tags">
            <Checkbox.Group
              value={selectedTags}
              onChange={handleTagChange}
              className={styles.customTagCheckbox}
            >
              {customCheckboxItem(
                rateValue > 3 ? satisfaction.tag4 : satisfaction.tag1,
              )}
            </Checkbox.Group>
          </Form.Item>
        )}
        {selectedTags.length > 0 && (
          <Form.Item name="description">
            <TextArea placeholder={formatMessage("evaluationText")} rows={3} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
});

export default memo(UserSatisfactionSurveyModal);
